import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { IconButton } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Inline } from "components/layout/reusableLayouts";

export interface SaveOrDiscardDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  /** Commit the pending edits, then continue. */
  onSave: () => void;
  /** Throw the pending edits away, then continue. */
  onDiscard: () => void;
  /** Stay where you are: the edits survive and nothing continues. */
  onCancel: () => void;
  saveLabel?: string;
  discardLabel?: string;
}

/**
 * Leaving a form with unsaved edits, where all three answers are real: save
 * and go, discard and go, or stay and keep editing.
 *
 * Deliberately not the DS `ConfirmDialog`, which is a confirmation: it has one
 * `onCancel` for its cancel button, its close affordance, the backdrop and
 * Escape alike, so "discard" and "stay" would collapse into each other and
 * one of the two outcomes would be unreachable. Everything else about the
 * shape is borrowed from it — a title row with a close affordance, and a
 * right-aligned footer — and the theme styles both.
 */
const SaveOrDiscardDialog: React.FC<SaveOrDiscardDialogProps> = ({
  open,
  title,
  message,
  onSave,
  onDiscard,
  onCancel,
  saveLabel,
  discardLabel,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      aria-labelledby="save-or-discard-title"
    >
      <DialogTitle id="save-or-discard-title">
        <Inline justify="between" align="center" wrap={false}>
          {title}
          <IconButton
            variant="ghost"
            onClick={onCancel}
            aria-label={t("Close")}
            size="small"
          >
            <HugeiconsIcon icon={Cancel01Icon} size={20} />
          </IconButton>
        </Inline>
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          sx={{ color: "var(--color-text-secondary)" }}
        >
          {message}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="default-outlined" onClick={onDiscard}>
          {discardLabel ?? t("Discard")}
        </Button>
        <Button variant="primary-filled" onClick={onSave}>
          {saveLabel ?? t("Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveOrDiscardDialog;
